/**
 * 窗屏內容發布（`admin.release_manifest` 權限）。
 *
 * 型別**手寫而非取自 `api-schema.d.ts`**：更新模擬回的是規格書 §15 的形狀（廠商
 * 訂的，鍵名如 `GPS`、`op`、`target_path`），後端刻意沒有為它宣告 DTO——那份形狀
 * 的權威是規格書與廠商韌體，在我們的 OpenAPI 裡再抄一份只會多一個會漂移的副本。
 */

import { apiFetch, buildQueryString, type Schemas } from './core';

export type DisplayPublishResult = Schemas['DisplayPublishResponseDto'];

/**
 * 發布一版窗屏內容：打包 → 簽章 → 立即切成 active。
 *
 * **不可逆**——`release_manifests` 是 append-only，發錯只能再發一版蓋過去
 * （或由後端 rollback 切回前一版），沒有「刪掉這一版」這種操作。呼叫端要先讓
 * 使用者確認過再打。
 *
 * 沒有參數：納入哪些景點由後端決定（已發布 + 有 roi_id），前端挑一半的景點發布
 * 會產生一個「船上有、岸上以為沒有」的版本。
 */
export function publishDisplayRelease(): Promise<DisplayPublishResult> {
  return apiFetch<DisplayPublishResult>('/admin/display-releases', { method: 'POST' });
}

/** 規格書 §15 的六個下載分類。 */
export interface CmsUpdateDownloadUrls {
  video: string[];
  pic: string[];
  wav: string[];
  txt: string[];
  keyword: string[];
  GPS: string[];
}

export interface CmsUpdateAttraction {
  op: string;
  attraction_id: number;
  attraction_name: string;
  roi?: string;
  target_path?: string;
  download_url?: CmsUpdateDownloadUrls;
  delete_paths?: string[];
}

export interface CmsUpdateInfo {
  version: string;
  update_required: number;
  attractions: CmsUpdateAttraction[];
}

export interface CmsUpdateEnvelope {
  status: string;
  server_time: string;
  lang: string;
  data: {
    /** 船機送的時間；沒送則為伺服器收到請求的時間。不會是 null。 */
    request_time: string;
    message: string;
    /** 失敗時規格要求給空物件。 */
    info: CmsUpdateInfo | Record<string, never>;
  };
}

/** `info` 有沒有內容——失敗時它是空物件而不是缺鍵。 */
export function hasUpdateInfo(info: CmsUpdateInfo | Record<string, never>): info is CmsUpdateInfo {
  return 'attractions' in info;
}

/**
 * 模擬船機此刻來問會拿到什麼。不寫入、不留遙測。
 *
 * `version` 留空 = 全新安裝。
 */
export function simulateCmsUpdate(params: {
  version?: string;
  lang?: string;
}): Promise<CmsUpdateEnvelope> {
  return apiFetch<CmsUpdateEnvelope>(
    `/admin/display-releases/update-simulation${buildQueryString(params)}`,
    { method: 'GET' },
  );
}
